import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTribunalStore } from "../store/tribunalStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";

/**
 * Procurement AI Tribunal (v1)
 * 
 * UI for managing procurement tribunal cases, vendor violations,
 * and automated tribunal decisions.
 */
export default function Tribunal() {
  const { t } = useTranslation();
  const {
    caseID,
    vendorID,
    severityScore,
    meshLinkHash,
    evidenceCount,
    jurisdiction,
    tribunalDecision,
    appealWindowDays,
    remediationDemandEUR,
    setCaseID,
    setVendorID,
    setSeverityScore,
    setMeshLinkHash,
    setEvidenceCount,
    setJurisdiction,
    generateDecision,
    appealCase,
    exportTribunalCase,
  } = useTribunalStore();

  // Derived values - must be defined before useEffect that uses them
  const weightedSeverity = useTribunalStore((s) => s.getWeightedSeverity());

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("TribunalPanel");
  }, []);

  // Update AI context inputs on change
  useEffect(() => {
    useAIContext.getState().setInput("vendorID", vendorID);
    useAIContext.getState().setInput("severityScore", severityScore);
    useAIContext.getState().setInput("evidenceCount", evidenceCount);
    useAIContext.getState().setInput("jurisdiction", jurisdiction);
    useAIContext.getState().setInput("tribunalDecision", tribunalDecision);
    useAIContext.getState().setInput("remediationDemandEUR", remediationDemandEUR);
    useAIContext.getState().setInput("appealWindowDays", appealWindowDays);
    useAIContext.getState().setInput("weightedSeverity", weightedSeverity);
  }, [vendorID, severityScore, evidenceCount, jurisdiction, tribunalDecision, remediationDemandEUR, appealWindowDays, weightedSeverity]);

  const handleGenerate = async () => {
    if (!vendorID || severityScore === 0) {
      setError(new Error(t("tribunal.invalidInput") || "Vendor ID and severity score are required"));
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await generateDecision();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("tribunal.generateFailed") || "Failed to generate decision");
      setError(error);
      console.error("Generate decision failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleAppeal = async () => {
    if (tribunalDecision === "pending" || appealWindowDays === 0) {
      setError(new Error(t("tribunal.cannotAppeal") || "Cannot appeal this case"));
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await appealCase();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("tribunal.appealFailed") || "Failed to appeal case");
      setError(error);
      console.error("Appeal case failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (tribunalDecision === "pending") {
      setError(new Error(t("tribunal.noDecision") || "No decision available to export"));
      return;
    }
    
    setError(null);
    try {
      exportTribunalCase();
    } catch (e) {
      const error = e instanceof Error ? e : new Error(t("tribunal.exportFailed") || "Failed to export tribunal case");
      setError(error);
      console.error("Export failed:", e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("tribunal.title") || "Procurement AI Tribunal"}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />
      
      {loading && <LoadingSpinner size="md" text={t("tribunal.processing") || "Processing..."} />}

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tribunal.vendorID") || "Vendor ID"}
          </label>
          <input
            type="text"
            value={vendorID}
            onChange={(e) => setVendorID(e.target.value)}
            placeholder="e.g., VENDOR-001"
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tribunal.severityScore") || "Severity Score (0-100)"}
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={severityScore}
            onChange={(e) => setSeverityScore(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tribunal.evidenceCount") || "Evidence Count"}
          </label>
          <input
            type="number"
            min="0"
            value={evidenceCount}
            onChange={(e) => setEvidenceCount(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tribunal.jurisdiction") || "Jurisdiction"}
          </label>
          <select
            value={jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value as any)}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          >
            <option value="EU">EU</option>
            <option value="National">National</option>
            <option value="EEA">EEA</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tribunal.meshLinkHash") || "Mesh Link Hash"}
          </label>
          <input
            type="text"
            value={meshLinkHash}
            onChange={(e) => setMeshLinkHash(e.target.value)}
            placeholder="e.g., 0x1234..."
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleGenerate}
          disabled={!vendorID || severityScore === 0 || loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (t("tribunal.processing") || "Processing...") : (t("tribunal.generate") || "Generate Decision")}
        </button>
        <button
          onClick={handleAppeal}
          disabled={tribunalDecision === "pending" || appealWindowDays === 0 || loading}
          className="px-4 py-2 bg-evat-neon/20 border border-evat-neon text-evat-neon rounded hover:bg-evat-neon/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("tribunal.appeal") || "Appeal Case"}
        </button>
      </div>

      {/* Tribunal Decision Display */}
      {tribunalDecision !== "pending" && (
        <div className="space-y-4 mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("tribunal.tribunalDecision") || "Tribunal Decision"}
              </div>
              <div className={`text-lg font-orbitron ${
                tribunalDecision === "disqualification" ? "text-red-400" :
                tribunalDecision === "fine" ? "text-yellow-400" :
                "text-orange-400"
              }`}>
                {tribunalDecision.toUpperCase()}
              </div>
            </div>

            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("tribunal.remediationDemandEUR") || "Remediation Demand"}
              </div>
              <div className="text-lg font-orbitron text-evat-gold">
                €{remediationDemandEUR.toLocaleString()}
              </div>
            </div>

            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("tribunal.appealWindowDays") || "Appeal Window"}
              </div>
              <div className="text-lg font-orbitron text-evat-neon">
                {appealWindowDays} days
              </div>
            </div>

            <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
              <div className="text-xs text-neutral-400 mb-1">
                {t("tribunal.weightedSeverity") || "Weighted Severity"}
              </div>
              <div className={`text-lg font-orbitron ${
                weightedSeverity >= 70 ? "text-red-400" :
                weightedSeverity >= 40 ? "text-yellow-400" :
                "text-green-400"
              }`}>
                {weightedSeverity.toFixed(1)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Metrics Display */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tribunal.jurisdiction") || "Jurisdiction"}
          </div>
          <div className="text-sm font-orbitron text-evat-gold">
            {jurisdiction}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tribunal.evidenceCount") || "Evidence Count"}
          </div>
          <div className="text-sm font-orbitron text-orange-400">
            {evidenceCount}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tribunal.severityScore") || "Severity Score"}
          </div>
          <div className={`text-sm font-orbitron ${
            severityScore >= 70 ? "text-red-400" :
            severityScore >= 40 ? "text-yellow-400" :
            "text-green-400"
          }`}>
            {severityScore}%
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          disabled={tribunalDecision === "pending" || loading}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("tribunal.export") || "Export Tribunal Case JSON"}
        >
          {t("tribunal.export") || "Export Tribunal Case JSON"}
        </button>
      </div>
    </div>
  );
}






